/**
 * taskGraphView — pure display shaping for the task dependency engine.
 *
 * `crm/taskGraph.ts` returns ids and structured reasons; the "Critical
 * path & slips" panel wants ordered task records and severity-ranked slip
 * rows. These helpers do exactly that translation and NOTHING else — no
 * store access, no recomputation of graph logic (callers pass in the
 * `analyzeTaskGraph` output), so they stay trivially testable and the
 * panel component stays thin.
 */

import type { ProjectTask } from '../db/types';
import type { TaskSlip } from './taskGraph';

const DAY_MS = 86_400_000;

/** Severity of a slip, worst reason wins. Drives the pill on the row. */
export type SlipSeverity = 'critical' | 'high' | 'watch';

/** One display-ready slip row for the panel. */
export interface SlipDisplay {
  taskId: string;
  title: string;
  /** Worst reason on the slip: overdue-blocked > blocker-overdue > due-soon. */
  severity: SlipSeverity;
  /** Whole days the task itself is past due (undefined when not overdue). */
  daysLate?: number;
  /** Human-readable reason lines, straight from the graph engine. */
  details: string[];
  /** True when the slipping task sits on the critical path. */
  onCriticalPath: boolean;
}

/** Sort weight per severity — critical first. */
const SEVERITY_RANK: Record<SlipSeverity, number> = {
  critical: 0,
  high: 1,
  watch: 2,
};

/** Whole-day delta between an ISO date (YYYY-MM-DD) and `today`. */
function daysUntil(dueIso: string, todayIso: string): number | undefined {
  const due = Date.parse(`${dueIso}T00:00:00Z`);
  const today = Date.parse(`${todayIso}T00:00:00Z`);
  if (Number.isNaN(due) || Number.isNaN(today)) return undefined;
  return Math.round((due - today) / DAY_MS);
}

/**
 * Map critical-path ids (from `criticalPath(...).ids`) to their task
 * records, preserving the blockers-first order. Ids without a matching
 * task (deleted mid-flight) are dropped, never an error.
 */
export function orderedCriticalPath(
  tasks: ProjectTask[],
  pathIds: string[],
): ProjectTask[] {
  const byId = new Map(tasks.map((t) => [t.id, t]));
  return pathIds
    .map((id) => byId.get(id))
    .filter((t): t is ProjectTask => t !== undefined);
}

/**
 * Shape `detectSlips` output into severity-ranked display rows.
 *
 * Severity = the worst reason on the slip:
 *   - `overdue-blocked`  → 'critical' (the task itself is already late)
 *   - `blocker-overdue`  → 'high'     (inherits lateness from a blocker)
 *   - `due-soon-blocked` → 'watch'    (at risk within the horizon)
 *
 * Rows sort critical-first, then most-days-late first, then by title so
 * the order is stable. Slips whose task vanished from the snapshot are
 * dropped.
 */
export function formatSlips(
  tasks: ProjectTask[],
  slips: TaskSlip[],
  criticalPathIds: string[] = [],
  today?: string,
): SlipDisplay[] {
  const byId = new Map(tasks.map((t) => [t.id, t]));
  const critical = new Set(criticalPathIds);

  const rows: SlipDisplay[] = [];
  for (const slip of slips) {
    const task = byId.get(slip.taskId);
    if (!task) continue;

    const kinds = new Set(slip.reasons.map((r) => r.kind));
    const severity: SlipSeverity = kinds.has('overdue-blocked')
      ? 'critical'
      : kinds.has('blocker-overdue')
        ? 'high'
        : 'watch';

    let daysLate: number | undefined;
    if (task.dueDate && today !== undefined) {
      const d = daysUntil(task.dueDate, today);
      if (d !== undefined && d < 0) daysLate = Math.abs(d);
    }

    rows.push({
      taskId: task.id,
      title: task.title,
      severity,
      daysLate,
      details: slip.reasons.map((r) => r.detail),
      onCriticalPath: critical.has(task.id),
    });
  }

  rows.sort((a, b) => {
    const s = SEVERITY_RANK[a.severity] - SEVERITY_RANK[b.severity];
    if (s !== 0) return s;
    const late = (b.daysLate ?? 0) - (a.daysLate ?? 0);
    if (late !== 0) return late;
    return a.title.localeCompare(b.title);
  });

  return rows;
}
